import {
  TextInput,
  Textarea,
  NumberInput,
  Autocomplete,
  TagsInput,
  Checkbox,
  Switch,
  FileInput,
  Radio,
  Stack,
  Text,
  Badge,
  Alert,
  Paper,
  Group,
} from "@mantine/core";
import { DateInput, DateTimePicker } from "@mantine/dates";
import {
  IconUpload,
  IconInfoCircle,
  IconPencilOff,
  IconLock,
} from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import type {
  FieldElement,
  FieldOption,
  LookupFieldType,
  LookupOption,
} from "@livingsyncs/types";
import { LOOKUP_FIELD_META } from "@livingsyncs/types";
import { TypedSignature } from "../signature/TypedSignature";
import { isFieldRequired } from "./engines/ConditionEngine";
import { reconcileMedicationSelection } from "./medicationLookupConstants";
import { formatDateOnly } from "../../utils/dateUtils";
import { EntityLookupField } from "./fields/EntityLookupField";
import { lookupValueLabel } from "./fields/lookupFieldValue";
import { BodyMapField } from "./fields/BodyMapField";

interface FieldRendererProps {
  element: FieldElement;
  value: unknown;
  onChange: (id: string, value: unknown) => void;
  readOnly: boolean;
  error?: string;
  values: Record<string, unknown>;
  /**
   * Real, backend-sourced choices for entity-lookup field types, keyed by
   * FieldType. A missing key (not just an empty array) means no context is
   * available on this surface (US-133 AC-6) — EntityLookupField renders a
   * disabled "resolved at fill time" state instead of a free-text fallback.
   */
  lookupOptions?: Partial<Record<LookupFieldType, LookupOption[]>>;
  /**
   * Lookup Drawer follow-up: reports this field's id when it gains focus, so
   * a host surface (DynamicFormRenderer) can track "the field the user is
   * currently filling" and insert Lookup Drawer results into it. Wired on
   * text/email/phone/textarea (free-text insertion) AND number (bare-numeric
   * insertion, e.g. a resident's latest weight reading into a "Weight (lbs)"
   * field — code-review fix, "couldn't insert weight here"). Deliberately
   * NOT select/checkbox/lookup types, whose value must match a constrained set.
   */
  onFieldFocus?: (fieldId: string) => void;
}

const OTHER_SENTINEL = "__other__";

/**
 * Deep-review fix: Mantine's Combobox hard-crashes ("Duplicate options are
 * not supported") on duplicate entries — legal under the old Select, which
 * keyed on unique VALUES, but the Autocomplete/TagsInput suggestions are
 * built from LABELS, which authors may repeat.
 */
function dedupeLabels(options: ReadonlyArray<{ label: string }>): string[] {
  return [...new Set(options.map((o) => o.label))];
}

/**
 * Trim + case-insensitive label match (deep-review fix): the mapping back to
 * the canonical option VALUE used to be an exact string comparison, so a
 * near-miss ("active" for label "Active", or a trailing space) silently
 * stored raw text and broke visibleIf/requiredIf conditions keyed on the
 * canonical value.
 */
function matchOptionByLabel<T extends { label: string }>(
  options: readonly T[],
  text: string,
): T | undefined {
  const needle = text.trim().toLowerCase();
  return options.find((o) => o.label.trim().toLowerCase() === needle);
}

interface RadioWithOtherProps {
  label: ReactNode;
  description?: string;
  options: FieldOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled: boolean;
}

/**
 * Radio group with an "Other" escape hatch — options are suggestions, not an
 * exhaustive list (same principle as the Autocomplete/TagsInput treatment of
 * select/multiselect below). Selecting "Other" reveals a text input; whatever
 * is typed there IS the field's stored value directly — indistinguishable
 * downstream from having typed that value into a matching option.
 */
function RadioWithOther({
  label,
  description,
  options,
  value,
  onChange,
  error,
  disabled,
}: RadioWithOtherProps) {
  const matchesKnownOption = options.some((o) => o.value === value);
  // Seeded once from the initial value — an "Other" value with no matching
  // option means the user is already mid-custom-entry (e.g. reopening a
  // partially-filled form); a later external reset of `value` isn't tracked
  // here, consistent with how DynamicFormRenderer's own `values` state is
  // itself only seeded once from initialData at mount.
  const [otherMode, setOtherMode] = useState(
    value !== "" && !matchesKnownOption,
  );

  return (
    <Stack gap="xs">
      <Radio.Group
        label={label}
        description={description}
        value={otherMode ? OTHER_SENTINEL : value}
        onChange={(v) => {
          if (v === OTHER_SENTINEL) {
            setOtherMode(true);
            onChange("");
          } else {
            setOtherMode(false);
            onChange(v);
          }
        }}
        error={error}
      >
        <Stack gap="xs" mt={4}>
          {options.map((opt) => (
            <Radio
              key={opt.value}
              value={opt.value}
              label={opt.label}
              disabled={disabled}
            />
          ))}
          <Radio value={OTHER_SENTINEL} label="Other" disabled={disabled} />
        </Stack>
      </Radio.Group>
      {otherMode && (
        <TextInput
          placeholder="Please specify"
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          disabled={disabled}
          ml="lg"
        />
      )}
    </Stack>
  );
}

export function FieldRenderer({
  element,
  value,
  onChange,
  readOnly,
  error,
  values,
  lookupOptions,
  onFieldFocus,
}: FieldRendererProps) {
  // A medication_lookup becomes an editable dropdown (sourced from the
  // resident's active meds) whenever options are supplied and we're not in
  // read-only view — even if the template marks it `locked`, since the value
  // is curated from the system list rather than free-typed.
  const medAsDropdown =
    element.type === "medication_lookup" &&
    lookupOptions?.medication_lookup !== undefined &&
    !readOnly;
  const required = isFieldRequired(
    element.validation?.required,
    element.requiredIf,
    values,
  );

  const label = (
    <Text component="span" size="sm" fw={500}>
      {element.label}
      {required && (
        <Text component="span" c="red" ml={2}>
          *
        </Text>
      )}
    </Text>
  );

  // Read-only or locked: render clean text values instead of disabled inputs.
  // A medication_lookup rendered as a dropdown (medAsDropdown) skips this.
  if (readOnly || (element.locked && !medAsDropdown)) {
    let displayValue: ReactNode = "—";

    if (element.type === "signature") {
      displayValue = (
        <Alert
          icon={<IconPencilOff size={14} />}
          color="gray"
          variant="light"
          p="xs"
        >
          <Text size="sm" c="dimmed">
            Signature on file — visible in downloaded PDF
          </Text>
        </Alert>
      );
    } else if (element.type === "file") {
      displayValue = (
        <Text size="sm" c="dimmed">
          File attachment — visible in downloaded PDF
        </Text>
      );
    } else if (
      element.type === "checkbox" &&
      element.options?.length &&
      typeof value !== "boolean" &&
      value !== "true" &&
      value !== "false"
    ) {
      // Checkbox-with-options is a multi-select finding group, not a
      // boolean — same list-of-selected-labels display as multiselect.
      // Boolean values fall through to the Yes/No branch below: submissions
      // saved before the multi-select fix stored a boolean against these
      // fields (deep-review fix — they showed "—" here otherwise).
      const selected = Array.isArray(value) ? value : [];
      displayValue = (
        <Text size="sm">
          {selected.length > 0
            ? element.options
                .filter((o) => selected.includes(o.value))
                .map((o) => o.label)
                .join(", ")
            : "—"}
        </Text>
      );
    } else if (element.type === "checkbox" || element.type === "switch") {
      displayValue = (
        <Text size="sm">
          {value === true || value === "true" ? "Yes" : "No"}
        </Text>
      );
    } else if (
      element.type === "multiselect" ||
      element.type in LOOKUP_FIELD_META
    ) {
      // Every entity-lookup type stores a label string (single) or string[]
      // (choose-many, e.g. medication_lookup/resident_allergy_lookup) —
      // lookupValueLabel formats either shape consistently.
      displayValue = (
        <Text size="sm">
          {lookupValueLabel(
            Array.isArray(value) || typeof value === "string"
              ? value
              : undefined,
          )}
        </Text>
      );
    } else if (element.type === "resident_dob") {
      // Show formatted date or a clear "not set" message for locked DOB fields
      if (value !== null && value !== undefined && value !== "") {
        displayValue = <Text size="sm">{formatDateOnly(String(value))}</Text>;
      } else if (element.locked && !readOnly) {
        displayValue = (
          <Text size="sm" c="dimmed" fs="italic">
            Not set in resident profile
          </Text>
        );
      } else {
        displayValue = <Text size="sm">—</Text>;
      }
    } else {
      const raw =
        value !== null && value !== undefined && value !== ""
          ? String(value)
          : "—";
      displayValue = <Text size="sm">{raw}</Text>;
    }

    return (
      <Stack gap={4}>
        <Group gap={6} align="center">
          <Text size="sm" fw={500} c="dimmed">
            {element.label}
          </Text>
          {element.locked && !readOnly && (
            <Badge
              size="xs"
              variant="light"
              color="blue"
              leftSection={<IconLock size={10} />}
            >
              Auto-filled
            </Badge>
          )}
        </Group>
        {element.description && (
          <Text size="xs" c="dimmed">
            {element.description}
          </Text>
        )}
        {/*
         * A locked field has no input, so a validation error on it would
         * otherwise be invisible — the submit guard blocks but the user sees
         * nothing (a dead Submit button). Surface it here and set aria-invalid
         * so scrollToFirstError can reach it (SCRUM-156).
         */}
        <Paper
          px="sm"
          py={6}
          withBorder
          // Fixed palette shade (identical in light & dark) — the dynamic form
          // always renders on a white-paper sheet (DynamicFormRenderer forces
          // light vars), so a scheme-aware `-light` here would go dark and is wrong.
          bg="gray.0"
          radius="sm"
          aria-invalid={error ? true : undefined}
          style={
            error ? { borderColor: "var(--mantine-color-red-6)" } : undefined
          }
        >
          {displayValue}
        </Paper>
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>
    );
  }

  const commonProps = {
    label,
    description: element.description,
    placeholder: element.placeholder,
    disabled: readOnly,
    error,
  } as const;

  switch (element.type) {
    case "text":
    case "email":
    case "phone":
      return (
        <TextInput
          {...commonProps}
          type={element.type === "phone" ? "tel" : element.type}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(element.id, e.currentTarget.value)}
          onFocus={() => onFieldFocus?.(element.id)}
        />
      );

    case "textarea":
      return (
        <Textarea
          {...commonProps}
          rows={element.rows ?? 4}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(element.id, e.currentTarget.value)}
          onFocus={() => onFieldFocus?.(element.id)}
        />
      );

    case "number":
      return (
        <NumberInput
          {...commonProps}
          value={typeof value === "number" ? value : ""}
          onChange={(v) => onChange(element.id, v)}
          min={element.min}
          max={element.max}
          step={element.step}
          // Code-review fix ("couldn't insert weight here"): number fields
          // WERE deliberately excluded from Lookup Drawer focus-tracking,
          // reasoning that a NumberInput can't hold a free-text sentence —
          // true for copyText, but a bare numericValue (e.g. a resident's
          // latest weight reading) fits a NumberInput exactly fine. Now
          // wired the same as text/textarea; DynamicFormRenderer's
          // handleInsert is what actually enforces number-vs-text only
          // accepting the appropriate representation.
          onFocus={() => onFieldFocus?.(element.id)}
        />
      );

    case "date":
      return (
        <DateInput
          {...commonProps}
          value={value ? new Date(String(value)) : null}
          onChange={(v) => onChange(element.id, v)}
          valueFormat="YYYY-MM-DD"
          clearable
        />
      );

    case "datetime":
      return (
        <DateTimePicker
          {...commonProps}
          value={value ? new Date(String(value)) : null}
          onChange={(v) => onChange(element.id, v)}
          clearable
        />
      );

    case "time":
      return (
        <TextInput
          {...commonProps}
          type="time"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(element.id, e.currentTarget.value)}
        />
      );

    case "select": {
      // Options are suggestions, not an exhaustive constraint — matching the
      // established Autocomplete pattern (ResidentForm.tsx/AppointmentForm.tsx:
      // "these are hints, NOT a fixed list"). Picking a listed choice stores
      // its canonical `value` (preserving visibleIf/requiredIf condition
      // compatibility with authored options), but any other typed text passes
      // through as-is instead of being silently discarded or forced back.
      const optionList = element.options ?? [];
      const selectedOption = optionList.find((o) => o.value === value);
      return (
        <Autocomplete
          {...commonProps}
          value={
            selectedOption
              ? selectedOption.label
              : typeof value === "string"
                ? value
                : ""
          }
          data={dedupeLabels(optionList)}
          onChange={(text) => {
            const match = matchOptionByLabel(optionList, text);
            onChange(element.id, match ? match.value : text);
          }}
        />
      );
    }

    case "multiselect": {
      // Same "suggestions, not exhaustive" principle as select, extended to
      // multiple free-typed tags via TagsInput (an established pattern
      // elsewhere in this app — PharmacyPartnerForm.tsx, CreateCaseModal.tsx).
      const optionList = element.options ?? [];
      const selectedValues = Array.isArray(value) ? (value as string[]) : [];
      const displayTags = selectedValues.map(
        (v) => optionList.find((o) => o.value === v)?.label ?? v,
      );
      return (
        <TagsInput
          {...commonProps}
          value={displayTags}
          data={dedupeLabels(optionList)}
          onChange={(tags) => {
            const mapped = tags.map((t) => {
              const match = matchOptionByLabel(optionList, t);
              return match ? match.value : t;
            });
            onChange(element.id, mapped);
          }}
        />
      );
    }

    case "radio":
      return (
        <RadioWithOther
          label={label}
          description={element.description}
          options={element.options ?? []}
          value={typeof value === "string" ? value : ""}
          onChange={(v) => onChange(element.id, v)}
          error={error}
          disabled={readOnly}
        />
      );

    case "checkbox":
      // Bug fix (hardening follow-up): a checkbox WITH options is a
      // multi-select "finding group" (e.g. dental findings) — exactly what
      // the AI prompt and Zod schema both document. This used to always
      // render a single boolean Checkbox regardless, silently discarding
      // element.options. Only a checkbox with no options is a real boolean.
      if (element.options && element.options.length > 0) {
        return (
          <Checkbox.Group
            label={label}
            description={element.description}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(v) => onChange(element.id, v)}
            error={error}
          >
            <Stack gap="xs" mt={4}>
              {element.options.map((opt) => (
                <Checkbox
                  key={opt.value}
                  value={opt.value}
                  label={opt.label}
                  disabled={readOnly}
                />
              ))}
            </Stack>
          </Checkbox.Group>
        );
      }
      return (
        <Checkbox
          label={element.label}
          description={element.description}
          checked={value === true || value === "true"}
          onChange={(e) => onChange(element.id, e.currentTarget.checked)}
          disabled={readOnly}
          error={error}
        />
      );

    case "switch":
      return (
        <Switch
          label={element.label}
          description={element.description}
          checked={value === true || value === "true"}
          onChange={(e) => onChange(element.id, e.currentTarget.checked)}
          disabled={readOnly}
          error={error}
        />
      );

    case "file":
      return (
        <FileInput
          {...commonProps}
          value={value instanceof File ? value : null}
          onChange={(f) => onChange(element.id, f)}
          accept={element.accept}
          multiple={element.multiple}
          leftSection={<IconUpload size={14} />}
          clearable
        />
      );

    case "signature":
      return (
        <Stack gap="xs">
          {label}
          {element.description && (
            <Text size="xs" c="dimmed">
              {element.description}
            </Text>
          )}
          {/*
           * The signature is the typed name stored as plain text, not the PNG
           * data URL the drawing pad produced. US-115's draft-resume still
           * works — the saved value IS the name, so it repopulates the field.
           */}
          <TypedSignature
            value={typeof value === "string" ? value : ""}
            onChange={(name) => onChange(element.id, name || null)}
            disabled={readOnly}
            data-testid={`signature-${element.id}`}
          />
          {error && (
            // aria-invalid makes the signature error reachable by
            // scrollToFirstError's [aria-invalid="true"] query, like every
            // Mantine input error.
            <Text size="xs" c="red" fw={500} aria-invalid="true" tabIndex={-1}>
              {error}
            </Text>
          )}
        </Stack>
      );

    case "resident_dob":
      return (
        <DateInput
          {...commonProps}
          placeholder={element.placeholder ?? "Date of birth"}
          value={
            value instanceof Date
              ? value
              : value
                ? new Date(String(value))
                : null
          }
          onChange={(d) => {
            if (!d) return onChange(element.id, null);
            // Use local date parts to avoid UTC off-by-one in negative-offset timezones
            const date = new Date(d as unknown as string);
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            onChange(element.id, `${y}-${m}-${day}`);
          }}
          rightSection={
            <Badge size="xs" variant="light">
              DOB
            </Badge>
          }
        />
      );

    // US-133 AC-2/AC-4: every entity-lookup type shares one real picker
    // component instead of a bespoke fallback per type. medication_lookup
    // keeps its reconcileMedicationSelection guard (the "no current
    // medications" sentinel can't coexist with a real selection); every
    // other lookup type stores/receives values directly.
    case "medication_lookup":
    case "resident_lookup":
    case "staff_lookup":
    case "facility_lookup":
    case "resident_allergy_lookup":
    case "resident_emergency_contact_lookup":
    case "resident_insurance_lookup":
    case "resident_pharmacy_lookup":
    case "resident_care_plan_lookup":
    case "external_provider_lookup": {
      // No runtime fallback needed: LOOKUP_FIELD_META (packages/types) is a
      // non-Partial Record keyed by every EntityPickerFieldType, so a
      // missing entry is a compile error there, not a silent gap here.
      const meta = LOOKUP_FIELD_META[element.type];
      const options = lookupOptions?.[element.type];
      return (
        <EntityLookupField
          {...commonProps}
          entityLabel={meta.entityLabel}
          multiple={meta.multiple}
          required={required}
          options={options}
          value={value as string | string[] | null | undefined}
          onChange={(v) =>
            onChange(
              element.id,
              element.type === "medication_lookup" && Array.isArray(v)
                ? reconcileMedicationSelection(v)
                : v,
            )
          }
        />
      );
    }

    case "calculated_field":
      return (
        <Stack gap={4}>
          {label}
          {element.description && (
            <Text size="xs" c="dimmed">
              {element.description}
            </Text>
          )}
          <Alert
            color="gray"
            variant="light"
            icon={<IconInfoCircle size={14} />}
            p="xs"
          >
            <Text size="sm">
              {value !== undefined && value !== null ? String(value) : "—"}
            </Text>
          </Alert>
        </Stack>
      );

    case "body_map":
      return (
        <BodyMapField
          label={label}
          description={element.description}
          disabled={readOnly}
          error={error}
          value={value}
          onChange={(v) => onChange(element.id, v)}
        />
      );

    default: {
      // Hardening follow-up: exhaustiveness guard. Every FieldType must have
      // its own case above — this line only compiles if `element.type` has
      // narrowed to `never` by this point. Forgetting a case for a new
      // FieldType (the exact mistake behind resident_pharmacy_lookup
      // silently rendering as free text before this fix) is now a build
      // error here instead of a silent fallback to a plain TextInput.
      const _exhaustive: never = element.type;
      void _exhaustive;
      return (
        <TextInput
          {...commonProps}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(element.id, e.currentTarget.value)}
        />
      );
    }
  }
}
